import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

type AnimatedMetricProps = {
  value: number;
  duration?: number;
  className?: string;
  formatter?: (value: number) => string;
};

export default function AnimatedMetric({
  value,
  duration = 1200,
  className,
  formatter,
}: AnimatedMetricProps) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValueRef = useRef(0);

  useEffect(() => {
    const startValue = previousValueRef.current;
    const endValue = safeValue;

    if (startValue === endValue) {
      setDisplayValue(endValue);
      setIsAnimating(false);
      return;
    }

    let frameId = 0;
    let startTime = 0;
    setIsAnimating(true);

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easedProgress;

      setDisplayValue(currentValue);

      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
        previousValueRef.current = endValue;
      }
    };

    frameId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [duration, safeValue]);

  const renderedText = formatter
    ? formatter(displayValue)
    : Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }).format(Math.round(displayValue));

  return (
    <span
      className={classNames(
        'tabular-nums transition-all duration-300',
        isAnimating && 'animate-pulse',
        className,
      )}
    >
      {renderedText}
    </span>
  );
}
